import {
  ChangeDetectionStrategy,
  Inject,
  Component,
  OnInit,
  ChangeDetectorRef,
  ElementRef,
} from '@angular/core';
import { AuthService } from '@app/shared/services/auth.service';
import { OnboardingStep } from '@app/onboarding/onboarding.enums';
import { WindowToken } from '@app/shared/window.token';
import { TipService } from '@app/onboarding/services/tip.service';
import { OnboardingConfigService } from '@app/onboarding/services/onboarding-config.service';
import { OnboardingStateService } from '@app/onboarding/services/onboarding-state.service';
import { TagsService } from '@app/tags/services/tags.service';
import { forkJoin } from 'rxjs';
import { TagsApi } from '@app/tags/tag-api.model';
import { TrackerService } from '@app/shared/services/tracker.service';
import { TrackerStep } from '@app/onboarding/components/tracker/tracker.component';
import { OnboardingService } from '@app/onboarding/services/onboarding.service';
import { ProductName } from '@app/layout/product-header.service';
import { Router } from '@angular/router';

/**
 * OnboardingView Component
 * Main landing for the onboarding router
 * View adds in footer/header for each view page
 *
 * <dgx-onboarding-view></dgx-onboarding-view>
 *
 */
@Component({
  selector: 'dgx-onboarding-view',
  templateUrl: './onboarding-view.component.html',
  styleUrls: ['./onboarding-view.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OnboardingViewComponent implements OnInit {
  public orgId: number;
  public templatePrefix: string;
  public loading: boolean = false;
  private currentStep: OnboardingStep;

  constructor(
    private authService: AuthService,
    private router: Router,
    private tipService: TipService,
    private onboardingConfigService: OnboardingConfigService,
    private onboardingStateService: OnboardingStateService,
    private onboardingService: OnboardingService,
    private tagsService: TagsService,
    private trackerService: TrackerService,
    private cdr: ChangeDetectorRef,
    private element: ElementRef,
    @Inject(WindowToken) private windowRef: Window
  ) {}

  public get context() {
    return ProductName.Onboarding;
  }

  public ngOnInit(): void {
    // TODO: [PD-90730] hides the refresh layout for onboarding for now
    const lxpRefreshNav = document.querySelector('dgx-lxp-nav');
    if (lxpRefreshNav) {
      lxpRefreshNav.setAttribute('style', 'display: none;');
    }

    this.orgId = this.authService.authUser.defaultOrgId;
    // initialive the service here for state subscribers
    this.onboardingConfigService.config.subscribe((config) => {
      this.templatePrefix = config.templatePrefix;
    });
    this.onboardingStateService.currentStep$.subscribe((step) => {
      this.currentStep = step;
    });
  }

  public nextStep(obj: { currentStep: OnboardingStep; buttonState }) {
    const { currentStep, buttonState } = obj;
    // If role select step update the tracking dots
    if (currentStep === OnboardingStep.roleselect) {
      const states = this.onboardingStateService.futureTrackerStates;
      this.onboardingStateService.updateTrackerStep(states);
    }

    const targetStepUrl = this.onboardingConfigService.getNextUrl(
      currentStep,
      this.templatePrefix
    );
    const targetStepName =
      this.templatePrefix === 'hmm-' &&
      currentStep === OnboardingStep.startLearning
        ? 'dashboard'
        : targetStepUrl.indexOf('index') > 0 ||
          targetStepUrl.indexOf('profile') > 0
        ? 'profile'
        : targetStepUrl.substring(12); // 12 is to remove:: '/getstarted/

    if (
      currentStep === OnboardingStep.startLearning &&
      this.templatePrefix === 'hmm-'
    ) {
      this.tipService.updateIntroStatus(true).subscribe(() => {
        this.trackStepCompleted(currentStep, targetStepName);

        // mark user as done when they see the done screen.
        // dashboard page has not yet been converted complete over to angular router so use the location href.
        this.trackerService.trackEventData({ action: 'Onboarding Completed' });
        this.submitUserInformation(targetStepUrl);
      });
    }
    // If not Hmm and on the last step of review, submit user information
    if (
      currentStep === OnboardingStep.review &&
      this.templatePrefix !== 'hmm-'
    ) {
      this.trackStepCompleted(currentStep, targetStepName);
      this.trackerService.trackEventData({ action: 'Onboarding Completed' });
      this.submitUserInformation(targetStepUrl);
    } else {
      this.trackStepCompleted(currentStep, targetStepName);
      this.router.navigateByUrl(
        buttonState?.nextUrl ? buttonState.nextUrl : targetStepUrl
      );
    }
  }

  public previousStep(obj: { currentStep: OnboardingStep; buttonState }) {
    const { currentStep, buttonState } = obj;
    const states = this.onboardingStateService.trackerSteps;
    const targetStepUrl = this.onboardingConfigService.getPreviousUrl(
      currentStep,
      states
    );
    const targetStepName = targetStepUrl.substring(12); // '/getstarted/

    this.router.navigateByUrl(targetStepUrl);

    this.trackerService.trackEventData({
      action: 'Onboarding Step Revisited',
      category: 'Onboarding',
      properties: {
        Step: currentStep,
        TargetStep: targetStepName,
      },
    });
  }

  public skipStep(obj: { currentStep: OnboardingStep; buttonState }) {
    const { currentStep, buttonState } = obj;
    // get future steps as sometimes tracker should not be updated until pressing next
    if (buttonState?.skipUrl) {
      if (currentStep === OnboardingStep.roleselect) {
        const states = this.onboardingStateService.futureTrackerStates;
        this.onboardingStateService.updateTrackerStep(states);
      }
      const targetStepName = buttonState.skipUrl.substring(12); // '/getstarted/
      this.router.navigateByUrl(buttonState.skipUrl);

      this.trackerService.trackEventData({
        action: 'Onboarding Step Skipped',
        category: 'Onboarding',
        properties: {
          Step: currentStep,
          TargetStep: targetStepName,
        },
      });
    }
  }

  public onTrackerClicked(step: TrackerStep) {
    this.trackerService.trackEventData({
      action: 'Dot Tracker Clicked',
      category: 'Onboarding',
      properties: {
        Step: this.currentStep,
        TargetStep: step.stepName,
      },
    });
  }

  private trackStepCompleted(
    currentStep: OnboardingStep,
    targetStepName: string
  ) {
    this.trackerService.trackEventData({
      action: 'Onboarding Step Completed',
      category: 'Onboarding',
      properties: {
        Step: currentStep,
        TargetStep: targetStepName,
      },
    });
  }

  /**
   * Submit user tags, job role, and user positions for the user and redirect to the next step(user profile skills)
   */
  private submitUserInformation(targetStepUrl: string) {
    // User can have 0 assigned skills
    const addedUserTags = this.onboardingStateService.addedTags;
    const onboardingAddedTags =
      this.onboardingStateService.onboardingUserAddedTags;
    // get all the skills the user has selected
    let allFollowingTags: Partial<TagsApi.Tag>[] = [
      ...addedUserTags,
      ...onboardingAddedTags,
    ];

    const userProfileTags = this.onboardingStateService.currentProfileSkills;
    allFollowingTags = [...allFollowingTags, ...userProfileTags];
    // Display a loading screen until it finally goes to the profile page.
    this.loading = true;
    this.cdr.detectChanges();
    const authUser = this.authService.authUser;

    //batch addUserTags (addUserInterests) calls to prevent large requests from getting Azure WAF 403 rejections
    let addUserTagsFuntions = [];
    const batchSize = 100;
    for (let i = 0; i < allFollowingTags.length; i += batchSize) {
      const batchAllFollowingTags = allFollowingTags.slice(i, i + batchSize);
      addUserTagsFuntions.push(
        this.tagsService.addUserTags(
          batchAllFollowingTags as TagsApi.Tag[],
          'Onboarding',
          // This prevents an error (Firefox Only) from popping up.
          // once angular routing on profile page is fully migrated
          // then this may not be needed.
          false
        )
      );
    }
    forkJoin([
      ...addUserTagsFuntions,
      this.tipService.updateJobRole(
        this.onboardingStateService.currentRole
          ? this.onboardingStateService.currentRole.name
          : authUser?.viewerProfile?.jobRole
      ),
      this.onboardingService.addUserExperiences(
        this.onboardingStateService.currentResume?.userPositions
      ),
      this.tipService.updateIntroStatus(true),
    ]).subscribe(
      () => {
        // profile page has not yet been converted complete over to angular router so use the location href.
        this.cdr.detectChanges();
        this.windowRef.open(targetStepUrl, '_self');
      },
      (error) => {
        this.loading = false;
      }
    );
  }
}
